import React from "react";
import { Facebook, Instagram, Youtube, Link2 } from "lucide-react";

interface SocialLink {
  id: number;
  url: string;
  newTab: boolean;
  text: string;
  social: string;
}

interface OfferText {
  id: number;
  text: string;
}

interface NavLink {
  id: number;
  url: string;
  newTab: boolean;
  text: string;
  dropdownLinks: null | any[];
}

interface TopBarProps {
  data: {
    id: number;
    text: string;
    isDisabledOfferSlider: boolean;
    isLive: boolean;
    socialLinks: SocialLink[];
    offersText: OfferText[];
    links: NavLink[];
  };
}

const TopBar2 = ({ data }: TopBarProps) => {
  if (!data?.isLive) return null;

  const getSocialIcon = (social: string) => {
    switch (social.toLowerCase()) {
      case "facebook":
        return <Facebook size={16} />;
      case "instagram":
        return <Instagram size={16} />;
      case "youtube":
        return <Youtube size={16} />;
      default:
        return <Link2 size={16} />;
    }
  };

  return (
    <div className="bg-black text-white py-2 sm:py-3 text-xs sm:text-sm border-b border-gray-700 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-5 flex flex-col md:flex-row items-center justify-between gap-2 md:gap-0">
        
        {/* Left Section */}
        <div className="flex items-center flex-wrap gap-2 md:gap-3 flex-1 justify-center md:justify-start z-10">
          <div className="font-semibold text-center md:text-left">
            {data.text}
          </div>

          <div className="flex gap-2 flex-wrap justify-center">
            {data?.socialLinks.map((social) => (
              <a
                key={social.id}
                href={social.url}
                target={social.newTab ? "_blank" : "_self"}
                rel={social.newTab ? "noopener noreferrer" : undefined}
                className="text-white p-1.5 rounded-sm bg-white/10 hover:bg-white/20 transition-colors"
                aria-label={social.social}
              >
                {getSocialIcon(social.social)}
              </a>
            ))}
          </div>
        </div>

        {/* Center Marquee */}
        {!data?.isDisabledOfferSlider && data?.offersText.length > 0 && (
          <div
            className="flex items-center flex-1 justify-center mt-2 md:mt-0 overflow-hidden w-full max-w-[300px] sm:max-w-md md:max-w-lg mx-auto"
            aria-live="polite"
          >
            <div className="animate-marquee">
              {[1, 2, 3, 4].map((set) =>
                data?.offersText.map((offer) => (
                  <span
                    key={`s${set}-${offer.id}`}
                    className="mx-4 sm:mx-8 whitespace-nowrap"
                  >
                    {offer.text}
                  </span>
                ))
              )}
            </div>
          </div>
        )}

        {/* Right Links */}
        <div className="flex-1 justify-center md:justify-end flex flex-wrap gap-2 md:gap-4 mt-2 md:mt-0 z-10">
          {data?.links.map((link) => (
            <a
              key={link.id}
              href={link.url}
              target={link.newTab ? "_blank" : "_self"}
              rel={link.newTab ? "noopener noreferrer" : undefined}
              className="text-white no-underline transition-colors hover:text-gray-400 text-xs sm:text-sm"
            >
              {link.text}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TopBar2;
